import '@awesome.me/webawesome/dist/components/badge/badge.js';
import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/tab/tab.js';
import '@awesome.me/webawesome/dist/components/tab-group/tab-group.js';
import '@awesome.me/webawesome/dist/components/tab-panel/tab-panel.js';
import '../components/breadcrumb.js';
import '../components/counter_badge.js';
import '../components/empty_state.js';
import '../components/error_callout.js';
import '../components/icon.js';

import { faEnvelope, faPlus, faUsers } from '@fortawesome/free-solid-svg-icons';
import { css, html, LitElement, nothing, type TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import {
    type IAdminAccount,
} from '../libraries/account_models.js';
import {
    inviteAdministrator,
    listAdministrationAccounts,
    removeAdministrator,
} from '../libraries/api_client/administration.js';
import {
    type IAdminInvitation,
    listAdministrationInvitations,
    withdrawAdministratorInvitation,
} from '../libraries/api_client/invitation.js';
import { valueOrThrow } from '../libraries/api_result.js';
import { getUserInfo } from '../libraries/auth.js';
import { ConfirmController } from '../libraries/confirm_controller.js';
import { withdrawInvitationConfirm } from '../libraries/confirm_presets.js';
import { formatGermanDate } from '../libraries/date_format.js';
import { formDialogHostStyles } from '../libraries/form_dialog_controller.js';
import { formOutcomeOf, type FormSubmitResult } from '../libraries/form_outcome.js';
import { inviteDialog } from '../libraries/form_presets.js';
import { invitationStatusBadge } from '../libraries/invitation_columns.js';
import { invitationSentMessage, invitationWithdrawnMessage } from '../libraries/invitation_copy.js';
import {
    type IListResult,
    type IListState,
    ListController,
    listHostStyles,
} from '../libraries/list_controller.js';
import {
    actionsColumn,
    type IListColumn,
} from '../libraries/list_table.js';
import { redirectTo } from '../libraries/navigation.js';
import { NoticeController } from '../libraries/notice_controller.js';
import { type IRowOutcomeSink, reportRowSuccess } from '../libraries/row_outcome.js';
import { StagedFormDialog } from '../libraries/staged_form_dialog.js';
import { resolveEffectiveTab } from '../libraries/tabs.js';
import { formatDisplayName } from '../libraries/user.js';

const noAccountsEmptyStateDescription =
    'Verwaltungskonten erscheinen hier, sobald eine Einladung angenommen wurde.';

// Both lists read the same administration surface, so a failure in either names
// it the same way.
const loadErrorDescription = 'Die Verwaltungsdaten konnten nicht geladen werden.';

export type AdminTab = 'accounts' | 'invitations';

/** What the open confirmation is about to delete. */
interface IConfirmTarget {
    type: 'remove' | 'withdraw';
    targetId: string;
    targetName: string;
}

/**
 * Administration page for managing admin accounts and admin invitations.
 * Accessible only to administrators via the user dropdown.
 *
 * Both tabs are always visible: the `requireAdmin` route guard gates the
 * whole page and `administration:manage` is the only administration
 * capability, so there is no per-tab permission split. `tabVisibility` and
 * `effectiveTab` mirror the organisation and farm detail pages so a deep link
 * to a non-visible tab would render the default tab instead of an empty
 * content area; under the current model the fallback never triggers.
 */
const adminInviteIntro =
    'Senden Sie eine Einladung an eine Person, um ein Verwaltungskonto einzurichten.';

@customElement('fm-admin')
export class FmAdmin extends LitElement {

    static styles = [
        listHostStyles,
        formDialogHostStyles,
        css`
        h1 {
            margin: 0 0 var(--wa-space-l) 0;
            font-size: var(--wa-font-size-l);
            font-weight: var(--wa-font-weight-bold);
            color: var(--fm-color-text-normal);
        }

        wa-tab-group {
            margin-bottom: var(--wa-space-l);
        }
    `,
    ];

    @property({
        type: String,
    })
    tab: AdminTab = 'invitations';

    /**
     * The feedback callout above the tab group. It stays the page's rather than
     * either list's notice, because a mutation on one tab reports above both.
     */
    private readonly notice = new NoticeController(this);

    /**
     * The removal and the withdrawal, which ask the same shape of question over two
     * different rows and so share one staged target.
     */
    private readonly confirmDialog = new ConfirmController<IConfirmTarget>(this, target => {
        const actions = {
            run: async (): Promise<FormSubmitResult> => this.runConfirmedAction(target),
            onSuccess: async (): Promise<void> => this.handleConfirmSuccess(target.type),
        };

        if (target.type === 'withdraw') {
            return withdrawInvitationConfirm(target.targetName, actions);
        }

        return {
            label: 'Verwaltungskonto entfernen',
            body: html`
                Soll <strong>${target.targetName}</strong> wirklich als Verwaltungskonto entfernt
                werden? Die Person verliert ihren Zugang zur Verwaltung.
            `,
            confirmLabel: 'Entfernen',
            confirm: actions.run,
            onSuccess: actions.onSuccess,
        };
    });

    private readonly currentUserEmail = getUserInfo()?.email ?? '';

    private readonly invitationsList = new ListController<IAdminInvitation>(this, {
        label: 'Einladungen',
        level: 'section',
        columns: (): Array<IListColumn<IAdminInvitation>> => this.invitationColumns,
        rowKey: (invitation): string => invitation.id,
        errorDescription: loadErrorDescription,
        load: async ({ signal }): Promise<IListResult<IAdminInvitation>> => ({
            rows: valueOrThrow(await listAdministrationInvitations({
                signal,
            })),
        }),
        empty: (): TemplateResult => this.renderInvitationsEmptyState(),
        actions: (listState): TemplateResult | typeof nothing => this.renderHeaderInviteButton(listState),
    });

    private readonly accountsList = new ListController<IAdminAccount>(this, {
        label: 'Verwaltungszugriff',
        level: 'section',
        columns: (): Array<IListColumn<IAdminAccount>> => this.accountColumns,
        rowKey: (account): string => account.id,
        errorDescription: loadErrorDescription,
        load: async ({ signal }): Promise<IListResult<IAdminAccount>> => ({
            rows: valueOrThrow(await listAdministrationAccounts({
                signal,
            })),
        }),
        empty: (): TemplateResult => this.renderAccountsEmptyState(),
    });

    /**
     * The single source of tab visibility. Both tabs are always visible (see
     * the class JSDoc), so the record is constant; it exists to keep the
     * effective-tab fallback identical to the other tab pages.
     * @returns Visibility per tab.
     */
    private get tabVisibility (): Record<AdminTab, boolean> {
        return {
            accounts: true,
            invitations: true,
        };
    }

    /**
     * The tab actually rendered: the tab named by the URL when visible, else
     * the default `invitations` tab. The URL is not rewritten.
     * @returns The effective tab.
     */
    private get effectiveTab (): AdminTab {
        return resolveEffectiveTab(this.tab, this.tabVisibility, 'invitations');
    }

    private readonly handleTabShow = (event: CustomEvent<{ name: string }>): void => {
        const { name } = event.detail;

        if (name === this.effectiveTab) {
            return;
        }

        switch (name) {
            case 'accounts':
                redirectTo('/administration/access');
                break;
            case 'invitations':
                redirectTo('/administration');
                break;
        }
    };

    /**
     * Opens the invite dialog from a pristine state. The page's own feedback from
     * a previous attempt is dismissed here; the form's messages and values are the
     * controller's, which holds them empty for as long as the dialog is closed.
     */
    private readonly openInviteDialog = (): void => {
        this.notice.dismiss();
        this.inviteDialog.stage();
    };

    private readonly inviteDialog = new StagedFormDialog(this, () => inviteDialog({
        intro: adminInviteIntro,
        submit: async (invitation): Promise<FormSubmitResult> => this.sendInvitation(invitation.email),
        onSuccess: async (): Promise<void> => this.finishInvitation(),
    }));

    /**
     * Sends one administration invitation.
     * @param email - The address to invite.
     * @returns What the request reported.
     */
    private async sendInvitation (email: string): Promise<FormSubmitResult> {
        return formOutcomeOf(await inviteAdministrator(email));
    }

    /**
     * Where a succeeded action on one of the two lists reports.
     *
     * The confirmation goes in the page's own notice rather than through
     * `ListController.confirmAndReload`, because this page has two lists and one
     * callout above both. Everything else about the tail is the same, so it is
     * {@link reportRowSuccess} rather than a third copy of it, the stale-rows sentence
     * included.
     * @param list - The list the action changed.
     * @returns The sink.
     */
    private sinkFor (list: { reload: () => Promise<boolean> }): IRowOutcomeSink {
        return {
            notify: (variant, message): void => this.notice.notify(variant, message),
            reload: list.reload,
        };
    }

    /**
     * Confirms the send and reloads the invitations. The dialog has closed itself by
     * now, the mechanic closing a staged one on success.
     */
    private readonly finishInvitation = async (): Promise<void> => {
        await reportRowSuccess(this.sinkFor(this.invitationsList), invitationSentMessage);
    };

    private async handleConfirmSuccess (type: 'remove' | 'withdraw'): Promise<void> {
        const removing = type === 'remove';

        await reportRowSuccess(
            this.sinkFor(removing ? this.accountsList : this.invitationsList),
            removing ? 'Verwaltungskonto entfernt.' : invitationWithdrawnMessage
        );
    }

    /**
     * Performs the confirmed deletion. Reports its refusal back to the dialog
     * rather than to the page's own callout, so the reason arrives where the user
     * is looking and the dialog stays open for another attempt.
     * @param dialog - What the open dialog is confirming.
     * @returns What the request reported.
     */
    private async runConfirmedAction (dialog: IConfirmTarget): Promise<FormSubmitResult> {
        return formOutcomeOf(dialog.type === 'remove' ?
            await removeAdministrator(dialog.targetId) :
            await withdrawAdministratorInvitation(dialog.targetId));
    }

    private renderAccountsEmptyState (): TemplateResult {
        return html`
            <fm-empty-state .icon=${faUsers}
                            heading="Keine Verwaltungskonten vorhanden"
                            description=${noAccountsEmptyStateDescription}>
            </fm-empty-state>
        `;
    }

    private get accountColumns (): Array<IListColumn<IAdminAccount>> {
        return [
            {
                label: 'Person',
                cell: account => `${account.firstName} ${account.lastName}`,
            },
            {
                label: 'E-Mail-Adresse',
                track: 'xl',
                cell: account => account.email,
            },
            actionsColumn<IAdminAccount>(account => html`
                    <wa-button size="s" variant="danger" appearance="outlined"
                               ?disabled=${account.email === this.currentUserEmail}
                               @click=${(): void => {
                                    this.confirmDialog.stage({
                                        type: 'remove',
                                        targetId: account.id,
                                        targetName: `${account.firstName} ${account.lastName}`,
                                    });
                                }}>
                        Entfernen
                    </wa-button>
                `),
        ];
    }

    /**
     * The invite CTA, which both the empty-state card and the heading row carry.
     * @param slot - The slot the button belongs in.
     * @returns The button.
     */
    private renderInviteButton (slot: string): TemplateResult {
        return html`
            <wa-button slot=${slot} variant="brand" size="s"
                       @click=${this.openInviteDialog}>
                <fm-icon slot="start" .icon=${faPlus}></fm-icon>
                Person einladen
            </wa-button>
        `;
    }

    private renderInvitationsEmptyState (): TemplateResult {
        return html`
            <fm-empty-state .icon=${faEnvelope}
                            heading="Keine ausstehenden Einladungen"
                            description="Laden Sie eine Person ein, um ein Verwaltungskonto einzurichten.">
                ${this.renderInviteButton('action')}
            </fm-empty-state>
        `;
    }

    /**
     * The heading row's CTA, left out until there is a table to sit above: the
     * empty-state card carries one itself.
     * @param listState - What the invitations list currently holds.
     * @returns The button, or nothing.
     */
    private renderHeaderInviteButton (listState: IListState<IAdminInvitation>): TemplateResult | typeof nothing {
        return listState.total === 0 ? nothing : this.renderInviteButton('actions');
    }

    private get invitationColumns (): Array<IListColumn<IAdminInvitation>> {
        return [
            {
                label: 'E-Mail-Adresse',
                track: 'xl',
                cell: inv => inv.email,
            },
            {
                label: 'Eingeladen am',
                cell: inv => formatGermanDate(inv.createdAt),
            },
            {
                label: 'Status',
                cell: inv => invitationStatusBadge(inv.status),
            },
            actionsColumn<IAdminInvitation>(inv => html`
                    <wa-button size="s" variant="danger" appearance="outlined"
                               @click=${(): void => {
                                    this.confirmDialog.stage({
                                        type: 'withdraw',
                                        targetId: inv.id,
                                        targetName: inv.email,
                                    });
                                }}>
                        Zurückziehen
                    </wa-button>
                `),
        ];
    }

    private readonly userDisplayName = formatDisplayName(getUserInfo());

    private renderInviteDialog (): TemplateResult | typeof nothing {
        return this.inviteDialog.render();
    }

    render (): TemplateResult {
        return html`
            <fm-breadcrumb .items=${[
                {
                    label: this.userDisplayName,
                },
            ]}></fm-breadcrumb>
            <h1>Verwaltung</h1>

            ${this.notice.render()}

            <wa-tab-group @wa-tab-show=${this.handleTabShow} active=${this.effectiveTab}>
                <wa-tab slot="nav" panel="invitations" ?active=${this.effectiveTab === 'invitations'}>
                    Einladungen
                    <fm-counter-badge .count=${this.invitationsList.rows.length}></fm-counter-badge>
                </wa-tab>
                <wa-tab slot="nav" panel="accounts" ?active=${this.effectiveTab === 'accounts'}>
                    Verwaltungszugriff
                </wa-tab>

                <wa-tab-panel name="invitations">
                    ${this.invitationsList.render()}
                </wa-tab-panel>

                <wa-tab-panel name="accounts">
                    ${this.accountsList.render()}
                </wa-tab-panel>
            </wa-tab-group>

            ${this.renderInviteDialog()}
            ${this.confirmDialog.render()}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fm-admin': FmAdmin;
    }
}
